import { Checkbox, TextField, TextFieldProps } from '@mui/material';
import FormControlLabel from 'further-ui/components/FormControlLabel';
import FormField from 'further-ui/components/FormField';
import CountrySelectorComponent, {
  CountrySelectorProps,
} from 'further-ui/components/CountrySelector';
import { Country } from 'further-ui/labels';
import { ComponentType, Fragment } from 'react';
import { Control, Controller, useFieldArray } from 'react-hook-form';

type Address = {
  address1: string;
  address2?: string;
  city: string;
  postcode: string;
  state?: string;
  country?: Country;
  hasBeenUsedForThreeYearsOrMore: boolean;
};

type Props = {
  addressFieldName: string;
  control: Control<Record<string, Array<Address>>>;
  disabled?: boolean;
  slots?: {
    TextField?: ComponentType<Omit<TextFieldProps, 'variant'>>;
    CountrySelector?: ComponentType<CountrySelectorProps>;
  };
};

const InvestorAddressList = ({
  control,
  addressFieldName,
  slots,
  disabled,
}: Props) => {
  const {
    fields: addresses,
    append,
    remove,
  } = useFieldArray({
    control,
    name: addressFieldName,
  });

  const TextFieldComponent = slots?.TextField ?? TextField;
  const CountrySelector = slots?.CountrySelector ?? CountrySelectorComponent;

  const handleAddressUsageChange = (index: number, checked: boolean) => {
    if (checked) {
      const indexesToRemove: Array<number> = [];
      for (let i = index + 1; i < addresses.length; i++) {
        indexesToRemove.push(i);
      }
      remove(indexesToRemove);
    } else {
      append({
        address1: '',
        address2: '',
        city: '',
        postcode: '',
        country: undefined,
        hasBeenUsedForThreeYearsOrMore: true,
      });
    }
  };

  return (
    <>
      {addresses.map((address, index) => {
        const addressLabel =
          index === 0 ? 'Current address' : 'Previous address';

        return (
          <Fragment key={address.id}>
            <FormField label={`${addressLabel} line 1`}>
              <Controller
                name={`${addressFieldName}.${index}.address1`}
                disabled={!!disabled}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextFieldComponent
                    {...field}
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder={`${addressLabel} line 1`}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </FormField>
            <FormField label={`${addressLabel} line 2`}>
              <Controller
                name={`${addressFieldName}.${index}.address2`}
                disabled={!!disabled}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextFieldComponent
                    {...field}
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder={`${addressLabel} line 2`}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </FormField>
            <FormField label={`${addressLabel} town/city`}>
              <Controller
                name={`${addressFieldName}.${index}.city`}
                disabled={!!disabled}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextFieldComponent
                    {...field}
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder={`${addressLabel} town/city`}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </FormField>
            <FormField label={`${addressLabel} postal code`}>
              <Controller
                name={`${addressFieldName}.${index}.postcode`}
                disabled={!!disabled}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextFieldComponent
                    {...field}
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder={`${addressLabel} postal code`}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </FormField>
            <FormField label={`${addressLabel} country`}>
              <Controller
                name={`${addressFieldName}.${index}.country`}
                disabled={!!disabled}
                control={control}
                render={({
                  field: { value, name, onChange },
                  fieldState: { error },
                }) => (
                  <CountrySelector
                    disabled={disabled}
                    value={value}
                    name={name}
                    onChange={(value) => {
                      onChange(value ?? null);
                    }}
                    error={error}
                    placeholder={`${addressLabel} country`}
                  />
                )}
              />
            </FormField>
            <FormField>
              <Controller
                name={`${addressFieldName}.${index}.hasBeenUsedForThreeYearsOrMore`}
                disabled={!!disabled}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    slotProps={{
                      typography: {
                        variant: 'body2',
                        color: 'grey.900',
                      },
                    }}
                    control={
                      <Checkbox
                        value={field.value}
                        name={field.name}
                        disabled={!!disabled}
                        size="small"
                        checked={field.value}
                        onChange={(_, checked) => {
                          field.onChange(checked);
                          handleAddressUsageChange(index, checked);
                        }}
                      />
                    }
                    label="Address has been the same for the last 3 years"
                  />
                )}
              />
            </FormField>
          </Fragment>
        );
      })}
    </>
  );
};

export default InvestorAddressList;
